import { getConfig } from "../config/config";
import tokenSignal from "../signals/Token.signal";
import sleep from "../libs/sleep";

const REACT_APP_API_ENDPOINT = getConfig('REACT_APP_API_ENDPOINT');

// Backend (PostgreSQL) LIMIT/OFFSET max; values above this cause 22003. Backend clamps; we log here to find the caller.
const MAX_SAFE_TAKE_SKIP = 2147483647;

function base64DecToArr(sBase64, nBlocksSize) {
  var sB64Enc = sBase64.replace(/[^A-Za-z0-9+/]/g, ''),
    nInLen = sB64Enc.length,
    nOutLen = nBlocksSize ? Math.ceil((nInLen * 3 + 1 >> 2) / nBlocksSize) * nBlocksSize : nInLen * 3 + 1 >> 2,
    aBytes = new Uint8Array(nOutLen);

  for (var nMod3, nMod4, nUint24 = 0, nOutIdx = 0, nInIdx = 0; nInIdx < nInLen; nInIdx++) {
    nMod4 = nInIdx & 3;
    nUint24 |= b64ToUint6(sB64Enc.charCodeAt(nInIdx)) << 18 - 6 * nMod4;
    if (nMod4 === 3 || nInLen - nInIdx === 1) {
      for (nMod3 = 0; nMod3 < 3 && nOutIdx < nOutLen; nMod3++, nOutIdx++) {
        aBytes[nOutIdx] = nUint24 >>> (16 >>> nMod3 & 24) & 255;
      }
      nUint24 = 0;
    }
  }
  return aBytes;
}

function b64ToUint6(nChar) {
  return nChar > 64 && nChar < 91 ? nChar - 65
    : nChar > 96 && nChar < 123 ? nChar - 71
      : nChar > 47 && nChar < 58 ? nChar + 4
        : nChar === 43 ? 62 : nChar === 47 ? 63 : 0;
}

function xFormBody(body) {
  if (body instanceof FormData) {
    return body;
  }

  if (typeof body === 'object') {
    return JSON.stringify(body);
  }

  return body;
}

const apiFetch = async ({
  path,
  body = undefined,
  method = 'GET',
  isUnAuthenticatedReq = false,
  headers = {},
  isFileDownload = false,
}, retries = 0) => {
  if (!tokenSignal.value.accessToken && !isUnAuthenticatedReq) {
    const errorObj = {
      message: 'Auth token not set',
      error: {
        path,
        body,
        method,
      },
    };
    console.error(errorObj);
    return [];
  }

  const methodToUse = method || (body ? 'POST' : 'GET');
  let res;

  try {
    res = await fetch(REACT_APP_API_ENDPOINT + path, {
      method: methodToUse,
      body: xFormBody(body),
      headers: {
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        appId: getConfig('APP_CONFIG').appId,
        Authorization:
          `Bearer ${tokenSignal.value.accessToken || ''}`,
        ...(body instanceof FormData
          ? {
          }
          : {
            'Content-Type': 'application/json',
          }),
        ...headers,
      },
    });
  } catch (error) {
    if (retries < 4) {
      await sleep((retries + 1) * (retries + 1) * 800);

      return apiFetch({
        path,
        body,
        method,
        isUnAuthenticatedReq,
        headers,
        isFileDownload,
      }, retries + 1);
    } else {
      throw error;
    }
  }

  if (isFileDownload) {
    const type = res.headers.get('content-type');
    const data = await res.text();
    const blob = new Blob([base64DecToArr(data)], { type });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const disposition = res.headers.get('content-disposition') || '';
    const fileNameMatch = disposition.match(/filename="?([^"]+)"?/i);
    const downloadName =
      fileNameMatch?.[1] ||
      (type && type.includes('text/csv') ? 'report.csv' : 'report.xlsx');
    link.setAttribute('download', downloadName);
    document.body.appendChild(link);
    link.click();
    return url;
  }

  let responseBody;
  let responseText = null;
  try {
    responseText = await res.text();
    responseBody = responseText ? JSON.parse(responseText) : null;
  } catch (parseError) {
    if (res.status >= 500 && retries < 4) {
      await sleep((retries + 1) * (retries + 1) * 800);
      return apiFetch({
        path,
        body,
        method,
        isUnAuthenticatedReq,
        headers,
        isFileDownload,
      }, retries + 1);
    }
    responseBody = null;
  }

  if (!res || res.status >= 400) {
    if (res.status >= 500 && retries < 4) {
      await sleep((retries + 1) * (retries + 1) * 800);

      return apiFetch({
        path,
        body,
        method,
        isUnAuthenticatedReq,
        headers,
        isFileDownload,
      }, retries + 1);
    }

    const errorPayload = responseBody?.data?.error ?? responseBody?.error ?? responseBody;
    const errorMessage =
      (typeof errorPayload?.message === 'string' && errorPayload.message) ||
      (typeof responseBody?.data?.message === 'string' && responseBody.data.message) ||
      (typeof responseBody?.error?.message === 'string' && responseBody.error.message) ||
      (typeof responseBody?.message === 'string' && responseBody.message) ||
      (typeof responseText === 'string' && responseText.length > 0 && responseText.length < 500 ? responseText : null);
    const prismaDetails = errorPayload?.prisma;

    const error = {
      message: errorMessage || `Request failed ${res.status}`,
      status: res.status,
      prisma: prismaDetails,
    };
    throw error;
  }

  return responseBody;
};

const get = async ({ path, options, headers = {}, isFileDownload = false, isUnAuthenticatedReq = false }, retries = 0) => {
  if (options && typeof window !== "undefined") {
    const take = options.take;
    const skip = options.skip;
    const takeBad = take !== undefined && (!Number.isFinite(Number(take)) || Number(take) < 0 || Number(take) > MAX_SAFE_TAKE_SKIP);
    const skipBad = skip !== undefined && (!Number.isFinite(Number(skip)) || Number(skip) < 0 || Number(skip) > MAX_SAFE_TAKE_SKIP);
    if (takeBad || skipBad) {
      console.warn("[CAAT API] take/skip out of safe range (would cause 22003) – backend will clamp. Fix the caller for this path.", {
        path,
        take: take !== undefined ? { value: take, maxAllowed: MAX_SAFE_TAKE_SKIP } : undefined,
        skip: skip !== undefined ? { value: skip, maxAllowed: MAX_SAFE_TAKE_SKIP } : undefined,
      });
    }
  }

  const queryString = new URLSearchParams({
    options: JSON.stringify(options),
  }).toString();
  const pathString = options ? `${path}?${queryString}` : path;

  // const timer = setTimeout(() => {
  //   alertSignal.update({
  //     type: 'notification',
  //     message: 'We are experiencing longer load times than usual...',
  //     variant: 'info',
  //     id: 'slow-load-time',
  //   });
  // }, 2000);

  const { data } = await apiFetch({
    path: pathString,
    headers,
    isFileDownload,
    isUnAuthenticatedReq,
  }, retries);

  // clearTimeout(timer);

  return data;
};

const post = async ({ path, body, isUnAuthenticatedReq = false }, retries = 0) => {
  const { data } = await apiFetch({
    path,
    body,
    method: 'POST',
    isUnAuthenticatedReq,
  }, retries);
  return data;
};

const patch = async ({ path, body }, retries = 0) => {
  const { data } = await apiFetch({ path, body, method: 'PATCH' }, retries);
  return data;
};

const put = async ({ path, body }, retries = 0) => {
  const { data } = await apiFetch({ path, body, method: 'PUT' }, retries);
  return data;
};

const deleteFunc = async ({ path, body }, retries = 0) => {
  const response = await apiFetch({ path, body, method: 'DELETE' }, retries);
  return response?.data ?? undefined;
};

/**
 * Fetch a binary resource (e.g. PDF) and return an object URL for preview.
 * Caller should revoke the URL when done: URL.revokeObjectURL(url).
 */
const getBlob = async ({ path }) => {
  if (!tokenSignal.value.accessToken) {
    throw { message: 'Auth token not set', status: 401 };
  }
  const res = await fetch(REACT_APP_API_ENDPOINT + path, {
    method: 'GET',
    headers: {
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      appId: getConfig('APP_CONFIG').appId,
      Authorization: `Bearer ${tokenSignal.value.accessToken || ''}`,
    },
  });
  if (!res || res.status >= 400) {
    const text = await res.text();
    let message = `Request failed ${res.status}`;
    try {
      const json = JSON.parse(text);
      const err = json?.data?.error ?? json?.error ?? json;
      if (err?.message) message = err.message;
    } catch (_) {
      if (text && text.length < 300) message = text;
    }
    throw { message, status: res.status };
  }
  const blob = await res.blob();
  return window.URL.createObjectURL(blob);
};

export default {
  apiFetch,
  get,
  getBlob,
  post,
  patch,
  put,
  delete: deleteFunc,
};
